import { IClient } from '../../../../Assembler/dist/registry/iface/iClient.js';
import type { ITransientStore } from '../../../../Assembler/dist/registry/iface/iTransientStore.js';
import type { GetParentJsonValueHandler } from '../../../../Assembler/dist/registry/iface/getParentJsonValueHandler.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { ScenarioTableFilter } from '../../registryCommon/dynamicData/scenarioTableFilter.js';

/**
 * Client-side CLIENT HANDLER for the AppSite-inventory TABLE (88* tier) — the "processor" half of the
 * two-handler split. Consumes the RAW enriched scenario-table island (produced by ScenarioTableClientData, the
 * server preparer) and filters / pages / projects it into the nested-table result WITHOUT a server round-trip.
 * The table twin of ScenarioClient.
 *
 * - Parse the raw enriched scenario-table island handed IN (never enumerated/fetched here — source-agnostic)
 * - Apply the client's live SelectedAppSiteFolder selection and paging state, REUSING the pure
 *   ScenarioTableFilter so the client tier is byte-identical to the server ScenarioTableData processData
 *
 * Distinct from ScenarioTableClientData (the server preparer): this is the client processor, registered on the
 * SEPARATE IClient registry. Depends ONLY on the pure filter (no IO, no ConfigFile), so the same body runs in
 * the browser bundle and in the console --apiclient/--ociclient simulation, certifying the browser artifact by
 * construction.
 */
export class ScenarioTableClient extends IClient {
    /**
     * Gets the full type name for client-handler operation tracking.
     */
    get typeName(): string {
        return 'ScenarioTableClient';
    }

    /**
     * Filters the RAW enriched scenario-table island by the client's live SelectedAppSiteFolder selection, then
     * paginates + projects the nested table, REUSING the pure ScenarioTableFilter so the client tier is
     * byte-identical to the server processData. The STATIC skips (SkipViewAppSitesFolder / SkipViewAppSites)
     * are already applied by the server preparer ScenarioTableClientData, so the raw handed IN is skip-filtered.
     * SelectedAppSiteFolder resolves configJson > componentJson > parent-delegate; paging (ItemsPerPage / PageNo)
     * resolves configJson > paramJson > default — IDENTICALLY to the server. Source-agnostic — the raw is handed
     * in (embedded data island / remote slice).
     */
    process(appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, transientStore: ITransientStore, configJson: JsonObject | null, getParentJsonValue: GetParentJsonValueHandler | null, rawJson: JsonObject): JsonObject {
        void appSite; void appView; void appFile; void componentName; void componentDepth; void resolvedAppSite; void resolvedComponentName; void transientStore;
        if (rawJson === null || rawJson === undefined || !rawJson.has('Scenarios') || !rawJson.get('Scenarios').isArray) {
            return new JsonObject();
        }

        const rows = ScenarioTableFilter.parseTableRows(rawJson.get('Scenarios').asArray());
        if (rows.length === 0) {
            return new JsonObject();
        }

        // Resolve SelectedAppSiteFolder IDENTICALLY to the server ScenarioTableData.processData:
        // configJson > componentJson > parent via delegate.
        let selectedFolder = '';
        if (configJson !== null && configJson !== undefined && configJson.has('SelectedAppSiteFolder') && configJson.get('SelectedAppSiteFolder').isString) {
            selectedFolder = configJson.get('SelectedAppSiteFolder').getString();
        } else if (componentJson !== null && componentJson !== undefined && componentJson.has('SelectedAppSiteFolder') && componentJson.get('SelectedAppSiteFolder').isString) {
            selectedFolder = componentJson.get('SelectedAppSiteFolder').getString();
        } else if (getParentJsonValue !== null) {
            selectedFolder = getParentJsonValue('SelectedAppSiteFolder') ?? '';
        }

        // Live paging state — same precedence the server uses (configJson > paramJson > default).
        const itemsPerPage = ScenarioTableFilter.resolveIntSetting(configJson, paramJson, 'ItemsPerPage', 0);
        const pageNo = ScenarioTableFilter.resolveIntSetting(configJson, paramJson, 'PageNo', 1);

        // Folder filter ONLY (static skips already baked into the raw by the preparer), then paginate + project.
        const filtered = ScenarioTableFilter.filterTableRows(rows, selectedFolder, '', '');
        return ScenarioTableFilter.buildTableResult(filtered, selectedFolder, itemsPerPage, pageNo);
    }
}
